import { useEffect } from "react";
import { useAtomValue } from "jotai";
import * as Sentry from "@sentry/react";
import {
  activeVendorAtom,
  atomStore,
  firstEnrolledVendor,
  getVendorCreds,
  setActiveSelection,
  setVendorProfile,
  updateVendorCreds,
  VendorAuthState,
  VendorId,
  vendorAuthStateAtoms,
} from "./vendor";
import { daysmartLogin } from "./daysmart/api";
import { recusLogin } from "./recus/api";
import { navigate } from ".";

const REFRESH_THRESHOLD_MS = 10 * 60 * 1000;

const ongoingAuths: Partial<Record<VendorId, Promise<VendorAuthState>>> = {};

export async function authenticate(
  vendor: VendorId,
  opts: {
    forceReauth?: boolean;
    credentials?: { username: string; password: string };
  } = {}
): Promise<VendorAuthState> {
  const stateAtom = vendorAuthStateAtoms[vendor];
  const current = atomStore.get(stateAtom);
  if (current && !opts.forceReauth && !opts.credentials) return current;
  if (ongoingAuths[vendor]) return ongoingAuths[vendor]!;

  const creds = opts.credentials ?? getVendorCreds(vendor);
  if (!creds || !creds.username || !creds.password) {
    throw new Error(`missing creds for ${vendor}`);
  }

  const login =
    vendor === "daysmart"
      ? () => daysmartLogin(creds.username, creds.password)
      : () => recusLogin(creds.username, creds.password);

  ongoingAuths[vendor] = login();
  try {
    const state = await ongoingAuths[vendor]!;
    updateVendorCreds(vendor, {
      username: creds.username,
      password: creds.password,
      attemptLogin: true,
    });
    setVendorProfile(vendor, state.user);
    atomStore.set(stateAtom, state);
    Sentry.setUser({ username: creds.username });
    return state;
  } finally {
    delete ongoingAuths[vendor];
  }
}

export async function switchToVendor(vendor: VendorId): Promise<void> {
  const creds = getVendorCreds(vendor);
  if (!creds) return;
  setActiveSelection(vendor);
  await authenticate(vendor);
  navigate("/");
}

export function useAuthenticate() {
  const activeVendor = useAtomValue(activeVendorAtom);

  const target: VendorId | null = activeVendor ?? firstEnrolledVendor();

  useEffect(() => {
    if (!target) {
      navigate("/auth");
      return;
    }
    const creds = getVendorCreds(target);
    if (!creds?.attemptLogin || !creds?.username || !creds?.password) {
      navigate("/auth");
      return;
    }
    authenticate(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => {
    if (!target) return;
    const retryAuth = () => {
      if (document.visibilityState === "hidden") return;
      const state = atomStore.get(vendorAuthStateAtoms[target]);
      if (!state) return;
      const ms = state.expiresAt.getTime() - Date.now();
      if (ms > REFRESH_THRESHOLD_MS) return;
      authenticate(target, { forceReauth: true });
    };
    document.addEventListener("visibilitychange", retryAuth);
    return () => document.removeEventListener("visibilitychange", retryAuth);
  }, [target]);
}
